import {UseFormSetValue} from 'react-hook-form';
import {t} from 'i18next';
import {FormValues} from '../schema';
import {IIngestionCopyResponse} from '../../../../api/types/ingestionApi.type';

type DropdownData = {
  sourceSystem: Array<{key: string; displayValue: string}>;
  serverLocation: Array<{key: string; displayValue: string}>;
  columnDelimeter: Array<{key: string; displayValue: string}>;
  dataDelimeter: Array<{key: string; displayValue: string}>;
  textQualifier: Array<{key: string; displayValue: string}>;
  dateFormat: Array<{key: string; displayValue: string}>;
  timeZone: Array<{key: string; displayValue: string}>;
  sourceFolderOptions: Array<{key: string; displayValue: string}>;
};

const INGESTION_TYPE_BY_OVERWRITE_VALUE: Record<
  string,
  {key: string; displayValue: string}
> = {
  Add: {key: '1', displayValue: t('ingestions.addOnly')},
  Overlay: {key: '2', displayValue: t('ingestions.overlay')},
};

export const getIngestionTypeFromOverwriteValue = (
  overwriteValue: string | null | undefined
) => INGESTION_TYPE_BY_OVERWRITE_VALUE[overwriteValue ?? ''] ?? null;

/**
 * Helper to find selected dropdown option for EDIT flow using "selected" field
 * Same logic as copy flow
 */
const findSelectedOption = (
  apiOptions: Array<{
    key: string;
    value: string;
    selected: string;
    text: string | null;
  }>,
  wizardOptions: Array<{key: string; displayValue: string}>
): {key: string; displayValue: string} | null => {
  if (!apiOptions || apiOptions.length === 0) return null;

  const selectedValue = apiOptions[0]?.selected;
  if (!selectedValue) return null;

  // Find the API option where value matches selected
  const selectedApiOption = apiOptions.find(
    (opt) => String(opt.value) === String(selectedValue)
  );

  if (!selectedApiOption) return null;

  // Match copy API's "key" with wizard dropdown's "displayValue"
  const matchedWizardOption = wizardOptions.find(
    (opt) => opt.displayValue === selectedApiOption.key
  );

  return matchedWizardOption || null;
};

/**
 * Prefill form for EDIT flow
 * Uses "selected" field logic (same as copy flow) for:
 * - Source System
 * - Server Location
 * - Field Separator
 * - Text Qualifier
 * - Multi-Value
 * - Date Format
 * - Source Folder (✅ NEW - only for edit flow)
 */
export const prefillFormWithLoadedData = (
  loadedData: IIngestionCopyResponse,
  setValue: UseFormSetValue<FormValues>,
  dropdowns: DropdownData
) => {
  const settings = loadedData.ingestionWizardModel.ingestionSpecifySetting;
  const apiDropdowns = loadedData.fillDropDownListFromSessionResult.result;

  // Set Ingestion Type
  const ingestionTypeOption = apiDropdowns.OverwriteOption?.find(
    (opt) => opt.value === settings.overwriteValue
  );
  if (ingestionTypeOption) {
    setValue('ingestionType', {
      key: ingestionTypeOption.value,
      displayValue: ingestionTypeOption.key,
    });
  } else {
    const overwriteValue = settings.overwriteValue || loadedData.overwriteValue;
    const fallbackIngestionType = getIngestionTypeFromOverwriteValue(overwriteValue);
    if (fallbackIngestionType) {
      setValue('ingestionType', fallbackIngestionType, {shouldDirty: false});
    }
  }

  // 1. Source System
  if (apiDropdowns.SourceSystem) {
    const selectedSourceSystem = findSelectedOption(
      apiDropdowns.SourceSystem,
      dropdowns.sourceSystem
    );
    if (selectedSourceSystem) {
      setValue('sourceSystem', selectedSourceSystem, {shouldDirty: false});
    }
  }

  // 2. Server Location
  if (apiDropdowns.ServerLocation) {
    const selectedServerLocation = findSelectedOption(
      apiDropdowns.ServerLocation,
      dropdowns.serverLocation
    );
    if (selectedServerLocation) {
      setValue('serverLocation', selectedServerLocation, {shouldDirty: false});
    }
  }

  // 3. Source Folder - ❌ REMOVED (handled in Phase 2)

  // 4. Field Separator
  if (apiDropdowns.ColumnDelimiter) {
    const selectedFieldSeparator = findSelectedOption(
      apiDropdowns.ColumnDelimiter,
      dropdowns.columnDelimeter
    );
    if (selectedFieldSeparator) {
      setValue('fieldSeparator', selectedFieldSeparator, {shouldDirty: false});
    }
  }

  // 5. Text Qualifier
  if (apiDropdowns.TextQualifier) {
    const selectedTextQualifier = findSelectedOption(
      apiDropdowns.TextQualifier,
      dropdowns.textQualifier
    );
    if (selectedTextQualifier) {
      setValue('textQualifier', selectedTextQualifier, {shouldDirty: false});
    }
  }

  // 6. Multi-Value
  if (apiDropdowns.DataDelimiter) {
    const selectedMultiValue = findSelectedOption(
      apiDropdowns.DataDelimiter,
      dropdowns.dataDelimeter
    );
    if (selectedMultiValue) {
      setValue('multiValue', selectedMultiValue, {shouldDirty: false});
    }
  }

  // 7. Date Format
  if (apiDropdowns.DateFormat) {
    const selectedDateFormat = findSelectedOption(
      apiDropdowns.DateFormat,
      dropdowns.dateFormat
    );
    if (selectedDateFormat) {
      setValue('dateFormat', selectedDateFormat, {shouldDirty: false});
    }
  }

  // 8. Time Zone
  if (settings.timeZoneValue) {
    const timeZoneOption = dropdowns.timeZone.find(
      (opt) => opt.key === settings.timeZoneValue
    );
    if (timeZoneOption) {
      setValue('timeZone', timeZoneOption, {shouldDirty: false});
    }
  }

  // ❌ REMOVED: Sources logic (handled in Phase 3)
};
